import { type ReadonlyVec2, vec2 } from 'gl-matrix';

import { type ItemOf } from '@canvas/type-utils';

import { type Direction, dirToVec2 } from '../direction/direction';

export type PartialVec2 = [number | undefined, number | undefined] | vec2;
export type ReadonlyPartialVec2 =
  | Readonly<[number | undefined, number | undefined]>
  | ReadonlyVec2;

// some extended functionality to go with gl-matrix's vec2
// please follow the api patterns established by gl-matrix

/**
 * Validates that an arbitrary value is a vec2.
 *
 * @param vec the value to validate
 * @returns `true` if the value is a vec2, `false` otherwise
 */
const validate = (vec: unknown): vec is vec2 => {
  if (vec == null || typeof vec !== 'object') {
    // Null, undefined, or not an object
    return false;
  }

  if (!('0' in vec) || !('1' in vec)) {
    // Not an array with two elements
    return false;
  }

  if (typeof vec[0] !== 'number' || typeof vec[1] !== 'number') {
    // Elements are not numbers
    return false;
  }

  if (isNaN(vec[0]) || isNaN(vec[1])) {
    // Elements are NaN
    return false;
  }

  return true;
};

/**
 * Validates that every element in an array is a valid vec2.
 *
 * @param arr The array to validate
 * @returns `true` if every element is a valid vec2, `false` otherwise
 */
export const validateArray = (arr: unknown[]): arr is vec2[] => {
  return Array.isArray(arr) && arr.every(validate);
};

/**
 * Converts a vec2 to a JSON-compatible format.
 *
 * @param vec the vec2 to convert
 * @returns An array with two numbers
 */
function toJSON(vec: ReadonlyVec2): [number, number];
function toJSON(vec: null | undefined): null | undefined;
function toJSON(
  vec: ReadonlyVec2 | null | undefined
): [number, number] | null | undefined {
  if (vec == null) {
    return vec;
  }

  return [vec[0], vec[1]];
}

/**
 * Safely compares two vec2s for equality, handling `null`, `undefined`, and other
 * non-vec2 values.
 *
 * @param a The first vec2 to compare
 * @param b The second vec2 to compare
 * @returns `true` if the vec2s are equal, `false` otherwise
 */
const safeEquals = (
  a: ReadonlyVec2 | null | undefined,
  b: ReadonlyVec2 | null | undefined
) => {
  if (!validate(a) || !validate(b)) {
    return a === b;
  }

  return vec2.equals(a, b);
};

/**
 * Computes the angle of a vec2 in radians
 * @example angle([1, 0]) will yield 0
 * @example angle([0, 1]) will yield Math.PI / 2
 * @example angle([-1, 0]) will yield Math.PI
 */
const angle = (a: ReadonlyVec2): number => {
  return Math.atan2(a[1], a[0]);
};

/**
 * Computes the angle of a vector drawn from one point to another
 * @example angleFrom([0, 0], [1, 0]) will yield 0
 * @example angleFrom([1, 1], [1, 2]) will yield Math.PI / 2
 */
const angleFrom = (from: ReadonlyVec2, to: ReadonlyVec2): number => {
  const difference = vec2.sub(vec2.create(), to, from);
  return angle(difference);
};

/**
 * Creates a vec2 from an angle in radians and a length
 * @example fromPolar(Math.PI / 2, 2) will yield [0, 2]
 */
const fromPolar = (angle: number, length = 1): vec2 => {
  const x = Math.cos(angle) * length;
  const y = Math.sin(angle) * length;
  return vec2.fromValues(x, y);
};

/**
 * Creates a vec2 representing a new point along an arc given its centre,
 * radius, angle (in radians) and optionally rotation angle.
 *
 * @param center the centre of the arc
 * @param radius the radius of the arc
 * @param angle the angle to use to determine the new point
 * @param rotationAngle an offset that gets _subtracted_ from `angle` to account
 * for differences in how the angle is measured
 * - _Example:_ our `PathSegmentArc` representation uses the angle of the tangent
 *   to the arc rather than the angle from the positive x-axis, which is
 *   perpendicular to the arc
 * - _Visualisation:_ passing in `Math.PI / 2` as `rotationAngle` would change an arc
 *   from ╯ to ╮
 */
const fromArcProperties = (
  center: ReadonlyVec2,
  radius: number,
  angle: number,
  rotationAngle = 0
) => {
  const point = fromPolar(angle - rotationAngle, radius);
  const pointWithOffset = vec2.fromValues(0, 0);
  vec2.add(pointWithOffset, point, center);
  return pointWithOffset;
};

/**
 * Calculates the centre of an arc from its angle (in radians) and radius
 */
const centerFromArc = (
  start: ReadonlyVec2,
  angleOfTangent: number,
  radius: number,
  counterClockwise = false
) => {
  // Turn the angle into a vec2 with direction tangential to the arc
  const directionVector = fromPolar(angleOfTangent, radius);
  // Rotate depending on whether it's clockwise or counter-clockwise
  const rotated = vec2.clone(directionVector);
  const rotationAngle = counterClockwise ? -Math.PI / 2 : Math.PI / 2;
  vec2.rotate(rotated, directionVector, [0, 0], rotationAngle);
  // Add rotated line to start pos
  const center = vec2.clone(rotated);
  vec2.add(center, start, rotated);
  return center;
};

/**
 * Finds the closest vec2 point to a target
 * @param points - an array of vec2s
 * @param target - a vec2 indicating the point position
 * @param radius - an optional radius to check around the point
 * @returns - number - the index of the matched point, or -1 if none found
 */
const findClosest = (
  points: ReadonlyVec2[],
  target: ReadonlyVec2,
  radius = Number.POSITIVE_INFINITY
): number => {
  const radiusSquared = radius * radius;

  let closestIndexSoFar = -1;
  let closestDistance = Number.POSITIVE_INFINITY;

  for (let i = 0; i < points.length; i++) {
    const distance = vec2.sqrDist(points[i], target);
    if (distance <= radiusSquared && distance < closestDistance) {
      closestIndexSoFar = i;
      closestDistance = distance;
    }
  }

  return closestIndexSoFar;
};

export const vec2Indexes = [0, 1] as const;
export type Vec2Indexes = (typeof vec2Indexes)[number];

export type CollectFn = {
  (callback: (i: Vec2Indexes) => number, vec?: undefined): vec2;
  <T extends Float32Array | Float64Array | [number, number]>(
    callback: (i: Vec2Indexes) => number,
    vec2: T
  ): T;
  <T extends Array<any>>(callback: (i: Vec2Indexes) => ItemOf<T>, array: T): T;
};
/**
 * Creates a vector values with a callback, called for each index.
 * @param callback Called once for each vector index. The returned value will
 * become the value for the given index.
 * @returns A vector based on the values returned from the index
 */
export const collect: CollectFn = <T extends Array<any>>(
  callback: (i: Vec2Indexes) => ItemOf<T>,
  vec: T = vec2.create() as unknown as T
) => {
  for (const i of vec2Indexes) {
    vec[i] = callback(i);
  }

  return vec;
};

export interface FallbackFn {
  (target: ReadonlyVec2 | ReadonlyPartialVec2, fallback: vec2): vec2;
  (target: ReadonlyVec2 | ReadonlyPartialVec2, fallback: ReadonlyVec2): ReadonlyVec2;
  (target: ReadonlyVec2 | ReadonlyPartialVec2, fallback: PartialVec2): PartialVec2;
  (
    target: ReadonlyVec2 | ReadonlyPartialVec2,
    fallback: ReadonlyPartialVec2
  ): ReadonlyPartialVec2;
}

/**
 * Returns true if a PartialVec2 only has partially defined values,
 * false if both values are defined
 *
 * @param v PartialVec2
 * @returns boolean
 */
export const isPartial = (v: PartialVec2): boolean => {
  return v[0] === undefined || v[1] === undefined;
};

/**
 * Replaces undefined values in {@link target} with the value in {@link fallback}
 */
export const fallback: FallbackFn = (partialVec, fallback) => {
  return partialVec.map(
    (value, index) => value ?? (fallback[index] as unknown as number)
  ) as vec2;
};

/**
 * gl-matrix defaults to using Float32Array (which can be altered globally to
 * use Float64Array).
 *
 * This method explicitly creates a float64 array.
 */
export const float64 = () => {
  // Note: gl-matrix supports using Float64Array (see
  // https://github.com/toji/gl-matrix/issues/192) but its types do not. We cast
  // to a [number, number] to avoid incompatability.
  return new Float64Array(2) as unknown as Float32Array;
};

export const float64FromValues = (x: number, y: number) => {
  const vec = float64();

  vec[0] = x;
  vec[1] = y;

  return vec;
};

export const oppositeIndex = (i: number) => (i === 0 ? 1 : 0);

export const findAveragePositionOfElements = (positions: vec2[]) => {
  let totalX = 0;
  let totalY = 0;

  positions.forEach((pos) => {
    totalX = pos[0] + totalX;
    totalY = pos[1] + totalY;
  });

  return vec2.fromValues(totalX / positions.length, totalY / positions.length);
};

export const average = (v0: vec2, v1: vec2, out = vec2.create()) => {
  out[0] = (v0[0] + v1[0]) / 2;
  out[1] = (v0[1] + v1[1]) / 2;

  return out;
};

export const shiftInDirection = (
  out: vec2,
  v: ReadonlyVec2,
  direction: Direction,
  amount: number
) => {
  return vec2.scaleAndAdd(out, v, dirToVec2(direction), amount);
};

export const vec2extended = {
  angle,
  angleFrom,
  centerFromArc,
  collect,
  fallback,
  findAveragePositionOfElements,
  findClosest,
  float64,
  float64FromValues,
  fromArcProperties,
  fromPolar,
  vec2Indexes,
  oppositeIndex,
  safeEquals,
  toJSON,
  validate,
  validateArray,
  midpoint: average,
  isPartial,
  shiftInDirection,
};

export function* zipNthDimension(
  vecNs: Iterable<ArrayLike<number>>,
  dimensionIndex: number
) {
  for (const vecN of vecNs) {
    yield vecN[dimensionIndex];
  }
}
